import React from "react";
import ButtonEdit from "./../../../components/Button/ButtonEdit";
import ButtonDelete from "./../../../components/Button/ButtonDelete";
import ButtonPrint from "./../../../components/Button/ButtonPrint";
import { getGroupPillClasses, isOutOfAllGroups } from "./helpers";

const VisualCards = ({
  rows,
  totalCount,
  visibleCount,
  onScroll,
  scrollRef,
  editingStatusId,
  setEditingStatusId,
  handleStatusChange,
  openChangeGroupModal,
  handleEditClick,
  handleDeleteClick,
  generateAdherentPdf,
  getStatusColor,
  activeSeason,
}) => {
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };
  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="p-3 sm:p-4 space-y-3 overflow-auto rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40 max-h-[72vh]"
    >
      {rows.map((a) => (
        <details
          key={a.id}
          className="group w-full rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
        >
          <summary className="list-none select-none">
            <div className="grid grid-cols-[120px_1fr] md:grid-cols-[160px_1fr] items-stretch w-full">
              <img
                src={toAbsolute(a.photo_url) || "/img/user_avatar.jpg"}
                alt=""
                className="block w-[120px] h-[120px] md:w-[160px] md:h-[160px] object-cover"
                loading="lazy"
              />
              <div className="p-3 sm:p-4 pb-2 min-w-0">
                <div className="text-slate-900 dark:text-slate-100 font-semibold tracking-tight truncate">
                  <span className="uppercase">{a.nom}</span>{" "}
                  <span className="capitalize font-medium">{a.prenom}</span>
                </div>
                <div className="text-[11px] text-slate-500">
                  {[
                    a.licence_number || null,
                    a.date_naissance
                      ? (() => {
                          const bad = isOutOfAllGroups(
                            a.date_naissance,
                            a.Groups,
                            activeSeason
                          );
                          const dateStr = new Date(
                            a.date_naissance
                          ).toLocaleDateString("fr-FR");
                          return (
                            <span
                              className={
                                bad ? "bg-red-600 text-white px-1 rounded" : ""
                              }
                              title={
                                bad
                                  ? "Âge incompatible avec le(s) groupe(s) assigné(s)"
                                  : ""
                              }
                            >
                              {dateStr}
                            </span>
                          );
                        })()
                      : null,
                  ]
                    .filter(Boolean)
                    .map((el, i) => (i > 0 ? [" • ", el] : [el]))
                    .flat()}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {editingStatusId === a.id ? (
                      <select
                        value={a.statut_inscription}
                        onChange={(e) =>
                          handleStatusChange(a.id, e.target.value)
                        }
                        onBlur={() => setEditingStatusId(null)}
                        className="rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-[12px]"
                        autoFocus
                      >
                        {[
                          "Licencié",
                          "Inscrit",
                          "Cours d'essai",
                          "Licencié - Arrêt",
                          "Non Licencié - Arrêt",
                          "Licencié Hors Club",
                          "Ancien Licencié",
                          "Ancien Licencié et Arrêt",
                        ].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-block px-2 py-1 whitespace-nowrap text-[11px] font-semibold rounded-md cursor-pointer ${getStatusColor(
                          a.statut_inscription
                        )}`}
                        onClick={() => setEditingStatusId(a.id)}
                        title="Cliquer pour modifier"
                      >
                        {a.statut_inscription}
                      </span>
                    )}
                  </div>

                  <div
                    className="flex flex-wrap gap-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      openChangeGroupModal(a.id);
                    }}
                    title="Gérer les groupes"
                  >
                    {(a.Groups || []).length > 0 ? (
                      a.Groups.map((g) => {
                        const { bg, text, ring } = getGroupPillClasses(g);
                        return (
                          <span
                            key={g.id || g.nom}
                            className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded ${bg} ${text} ${ring} hover:opacity-90`}
                          >
                            {g.nom}
                          </span>
                        );
                      })
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded bg-rose-600 text-white hover:opacity-90">
                        Sans groupe (clic)
                      </span>
                    )}
                  </div>

                  <div className="pointer-events-none">
                    <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 bg-white/70 dark:bg-slate-800/70">
                      <span className="text-slate-500">Contacts & actions</span>
                      <span className="group-open:hidden">▼</span>
                      <span className="hidden group-open:inline">▲</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </summary>

          <div className="bg-gray-900 text-white -mt-px px-3 sm:px-4 py-3">
            <div className="grid gap-3 grid-cols-1 md:grid-cols-[1fr_auto]">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-gray-300/90">
                  Email
                </div>
                <div className="text-sm truncate">{a.email || "—"}</div>
                <div className="text-[12px] text-gray-300 truncate">
                  {(a.Users || [])
                    .map((u) => `${u.nom} ${u.prenom}`)
                    .join(", ") || "—"}
                  {" • "}
                  {a.createdAt
                    ? new Date(a.createdAt).toLocaleDateString("fr-FR")
                    : "—"}
                </div>
              </div>

              <div className="flex md:justify-end items-center gap-2">
                <ButtonPrint onClick={() => generateAdherentPdf(a)} />
                <ButtonEdit onClick={() => handleEditClick(a)} />
                <ButtonDelete onClick={() => handleDeleteClick(a.id)} />
              </div>
            </div>
          </div>
        </details>
      ))}

      {visibleCount < totalCount && (
        <div className="p-3 text-center text-xs text-slate-500">
          Affichage de {visibleCount} / {totalCount} — descendez pour charger la
          suite…
        </div>
      )}
    </div>
  );
};

export default VisualCards;
